import { useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";
import {
  InstallmentStatusEnum,
  type Installment,
} from "@/generated/types";
import { PROJECT_CONTRACTS_QUERY } from "@/hooks/useProjectContracts";
import { PROJECT_FINANCIAL_SUMMARY_QUERY } from "@/hooks/useProjectFinancialSummary";
import { parseIsoDate, todayIsoDate } from "@/lib/dateInput";
import type { MutationCallbacks } from "@/hooks/useMarkInstallmentAsPaid";

export const UNMARK_INSTALLMENT_AS_PAID_MUTATION = gql`
  mutation UnmarkInstallmentAsPaid($id: ID!) {
    unmarkInstallmentAsPaid(id: $id) {
      installment {
        id
        number
        amount
        dueDate
        status
        paidAt
      }
    }
  }
`;

export function optimisticStatus(dueDate: string): InstallmentStatusEnum {
  const due = parseIsoDate(dueDate);
  const today = parseIsoDate(todayIsoDate());
  if (!due || !today) return InstallmentStatusEnum.Pending;

  return due < today
    ? InstallmentStatusEnum.Overdue
    : InstallmentStatusEnum.Pending;
}

type UseUnmarkInstallmentAsPaidOptions = MutationCallbacks;

export function useUnmarkInstallmentAsPaid(
  projectId: string,
  options?: UseUnmarkInstallmentAsPaidOptions
) {
  const [mutate, { loading, error }] = useMutation(UNMARK_INSTALLMENT_AS_PAID_MUTATION, {
    refetchQueries: [
      { query: PROJECT_CONTRACTS_QUERY, variables: { projectId } },
      { query: PROJECT_FINANCIAL_SUMMARY_QUERY, variables: { projectId } },
    ],
  });

  const unmarkAsPaid = (
    installment: Pick<Installment, "id" | "number" | "amount" | "dueDate">,
    callbacks?: MutationCallbacks
  ) => {
    mutate({
      variables: { id: installment.id },
      optimisticResponse: {
        unmarkInstallmentAsPaid: {
          __typename: "UnmarkInstallmentAsPaidPayload",
          installment: {
            __typename: "Installment",
            id: installment.id,
            number: installment.number,
            amount: installment.amount,
            dueDate: installment.dueDate,
            status: optimisticStatus(String(installment.dueDate)),
            paidAt: null,
          },
        },
      },
      onCompleted: () => {
        options?.onCompleted?.();
        callbacks?.onCompleted?.();
      },
      onError: (mutationError) => {
        options?.onError?.(mutationError);
        callbacks?.onError?.(mutationError);
      },
    });
  };

  return { unmarkAsPaid, loading, error };
}
